import { useState, useEffect, useRef, Suspense } from 'react';
import { lazyScreen, prefetchScreens } from './utils/lazyScreen';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { OrganizationProvider, useOrganization } from './contexts/OrganizationContext';
import { ChatProvider } from './contexts/ChatContext';
import { PermissionsProvider } from './contexts/PermissionsContext';
import { ModuleAccessProvider, ModuleGate, useModuleAccess } from './contexts/ModuleAccessContext';
import { ChatPopupProvider } from './contexts/ChatPopupContext';
import { KanbanTimeTrackingProvider } from './contexts/KanbanTimeTrackingContext';
import { TagsProvider } from './contexts/TagsContext';
import { ActiveSectionProvider } from './contexts/ActiveSectionContext';
import { ToastProvider } from './contexts/ToastContext';
import { ConfirmProvider } from './contexts/ConfirmContext';
// Importar componentes do chat diretamente para debug
import ChatPopup from './components/Chat/ChatPopup';
import ChatFloatingButton from './components/Chat/ChatFloatingButton';
import ChatToastNotifications from './components/Chat/ChatToastNotifications';
import KanbanTimeTrackingPopup from './components/Activities/KanbanTimeTrackingPopup';
import SignInForm from './components/Auth/SignInForm';
import SignUpForm from './components/Auth/SignUpForm';
import { PUBLIC_SIGNUP_ENABLED } from './config/signup';
import ResetPassword from './components/Auth/ResetPassword';
const CompanyProfile = lazyScreen(() => import('./components/Company/CompanyProfile'));
import { firstAllowedSection } from './utils/modules';
import { ProtectedRoute } from './components/ProtectedRoute';
// import CompanySettings from './components/Company/CompanySettings';
const AccessManagement = lazyScreen(() => import('./components/Company/AccessManagement'));
// import ProjectManagement from './components/Projects/ProjectManagement';
const CompanyNotifications = lazyScreen(() => import('./components/Company/CompanyNotifications'));
import Sidebar from './components/Sidebar';
import Header from './components/Header';
const ProjectOverview = lazyScreen(() => import('./components/Projects/ProjectOverview'));
const InboxView = lazyScreen(() => import('./components/TaskList/InboxView'));
// Telas carregam sob demanda: só o que aparece em toda página (layout, chat, IA) vai no bundle inicial
const SimpleKanbanBoard = lazyScreen(() => import('./components/Activities/SimpleKanbanBoard'));
const NankinInsights = lazyScreen(() => import('./components/NankinInsights/NankinInsights'));
const ActivitiesCalendar = lazyScreen(() => import('./components/Activities/Calendar/ActivitiesCalendar'));
const ExternalTimeTracker = lazyScreen(() => import('./components/TimeTracking/ExternalTimeTracker'));
// import TaskList from './components/TaskList/TaskList';
const TodayView = lazyScreen(() => import('./components/TaskList/TodayView'));
const UpcomingView = lazyScreen(() => import('./components/TaskList/UpcomingView'));
const UnifiedTaskView = lazyScreen(() => import('./components/TaskList/UnifiedTaskView'));
const MembersList = lazyScreen(() => import('./components/Members/MembersList'));
const TagsAndFilters = lazyScreen(() => import('./components/TaskList/TagsAndFilters'));
const ProjectsList = lazyScreen(() => import('./components/Projects/ProjectsList'));
const WelcomeWorkflow = lazyScreen(() => import('./components/GettingStarted/WelcomeWorkflow'));
const Settings = lazyScreen(() => import('./components/Settings/Settings'));
const Clients = lazyScreen(() => import('./components/Clients/Clients'));
const Team = lazyScreen(() => import('./components/Team/Team'));
const TagsManager = lazyScreen(() => import('./components/TimeTracking/Tags/TagsManager'));
const CalendarView = lazyScreen(() => import('./components/TimeTracking/Calendar/CalendarView'));
// import InsightsView from './components/TimeTracking/Insights/InsightsView';
const UnifiedReports = lazyScreen(() => import('./components/TimeTracking/Reports/UnifiedReports'));
// import SimpleTimeReport from './components/TimeTracking/Reports/SimpleTimeReport';
const CompletedTasksView = lazyScreen(() => import('./components/TaskList/CompletedTasksView'));
const CreateFavoritesTable = lazyScreen(() => import('./components/Admin/CreateFavoritesTable'));
const TimeControl = lazyScreen(() => import('./components/TimeTracking/TimeControl'));
// import Profile from './components/Profile/Profile';
// import ActivityMonitoring from './components/Activities/Monitoring/ActivityMonitoring';
const TaskSummaryView = lazyScreen(() => import('./components/TaskList/TaskSummaryView'));
const ProfileManagement = lazyScreen(() => import('./components/Profile/ProfileManagement'));
const HelpScreen = lazyScreen(() => import('./components/Help/HelpScreen'));
const FaqManager = lazyScreen(() => import('./components/Help/FaqManager'));
import NankinAIChat from './components/NankinAI/NankinAIChat';
import { HelmetProvider } from 'react-helmet-async';
import { MetaTitle } from './components/MetaTitle';
import { supabase } from './lib/supabase';
const SimpleTimeTracker = lazyScreen(() => import('./components/TimeTracking/SimpleTimeTracker'));
const OverdueTasks = lazyScreen(() => import('./components/TaskList/OverdueTasks'));
const StrategicDashboard = lazyScreen(() => import('./components/Activities/Strategic/StrategicDashboard'));
const GanttViewOptimized = lazyScreen(() => import('./components/Activities/Strategic/GanttViewOptimized'));
const TaskInsights = lazyScreen(() => import('./components/TaskList/TaskInsights'));
const TasksPage = lazyScreen(() => import('./pages/TasksPage'));
const SalesPipelineBoard = lazyScreen(() => import('./components/SalesPipeline/SalesPipelineBoard'));
const UnifiedFinancialMovements = lazyScreen(() => import('./components/Finance/UnifiedFinancialMovements'));
const FinancialProjects = lazyScreen(() => import('./components/Finance/FinancialProjects'));
const FinancialRegisters = lazyScreen(() => import('./components/Finance/FinancialRegisters'));
const Reconciliation = lazyScreen(() => import('./components/Finance/Reconciliation'));
const BankAccountsManager = lazyScreen(() => import('./components/Finance/BankAccountsManager'));
const FinancialCalendar = lazyScreen(() => import('./components/Finance/FinancialCalendar'));
const FinancialDashboard = lazyScreen(() => import('./components/Finance/FinancialDashboard'));
import { RouteTracker } from './components/RouteTracker';
const ServicesManager = lazyScreen(() => import('./components/ProductSpec/ServicesManager'));
const ServiceTypesManager = lazyScreen(() => import('./components/ProductSpec/ServiceTypesManager'));
const BudgetDashboard = lazyScreen(() => import('./components/ProductSpec/BudgetDashboard'));
const PriceSimulator = lazyScreen(() => import('./components/Commercial/PriceSimulator'));
import { VersionDisplay } from './components/Version/VersionDisplay';
import { VersionUpdateNotification } from './components/Version/VersionUpdateNotification';
const TestProductsConnection = lazyScreen(() => import('./components/Projects/TestProductsConnection'));
const TestCategoriesConnection = lazyScreen(() => import('./components/Projects/TestCategoriesConnection'));
const SuppliersClientsManager = lazyScreen(() => import('./components/Projects/SuppliersClientsManager'));

interface Project {
  id: string;
  name: string;
  type: 'design' | 'development' | 'marketing';
}

const getSectionFromPath = (pathname: string) => {
  // Remove a barra inicial e pega a primeira parte da rota
  const section = pathname.split('/')[1];
  return section || 'projects';
};

// Fora do componente: definido dentro, virava um tipo novo a cada render e remontava.
// Ocupa só a área de conteúdo; o menu e o cabeçalho continuam na tela enquanto a tela carrega.
const LoadingFallback = () => (
  <div className="flex items-center justify-center py-24" role="status">
    <div className="flex flex-col items-center gap-3">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-coral-deep"></div>
      <p className="text-sm text-gray-500">Carregando...</p>
    </div>
  </div>
);

const ProtectedLayout = () => {
  const { user, loading } = useAuth();
  const { organization } = useOrganization();
  const navigate = useNavigate();
  const location = useLocation();
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const moduleAccess = useModuleAccess();
  const landingChecked = useRef(false);

  // Na primeira carga (rota padrão ou última rota restaurada), se o cargo não
  // libera essa tela, vai para a primeira tela liberada em vez de "Sem acesso".
  useEffect(() => {
    if (landingChecked.current || moduleAccess.loading || !user?.id || !organization?.id) return;
    landingChecked.current = true;
    const current = getSectionFromPath(location.pathname);
    if (!moduleAccess.canAccess(current)) {
      const target = firstAllowedSection(moduleAccess);
      if (target) navigate(`/${target}`, { replace: true });
    }
  }, [moduleAccess, user?.id, organization?.id, location.pathname, navigate]);

  // Depois do login, baixa o código dos outros menus em segundo plano.
  useEffect(() => {
    if (user?.id) prefetchScreens();
  }, [user?.id]);

  // Função para buscar projetos do Supabase
  const fetchProjects = async () => {
    if (!organization?.id) return;
    
    try {
      setProjectsLoading(true);
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('organization_id', organization.id);
        
      if (error) throw error;
      
      if (data) {
        // Adaptar projetos para o formato esperado pelo Sidebar
        const adaptedProjects = data.map(project => ({
          id: project.id,
          name: project.name,
          // Manter compatibilidade com ambas as estruturas
          type: project.status === 'archived' ? 'archived' : 'design',
          status: project.status || 'active'
        })) as Project[];
        
        setProjects(adaptedProjects);
      }
    } catch (err) {
      console.error('Erro ao buscar projetos:', err);
    } finally {
      setProjectsLoading(false);
    }
  };

  // Buscar projetos quando o usuário ou organização mudar
  useEffect(() => {
    if (user && organization?.id) {
      fetchProjects();
    } else {
      // Se não há usuário ou organização, não precisa carregar projetos
      setProjectsLoading(false);
    }
    // Depender de `user?.id` (não do objeto `user`): o Supabase revalida a sessão
    // sempre que a aba volta a ficar visível e emite um objeto `user` novo (mesma
    // pessoa, referência diferente) — dependendo do objeto inteiro, esse efeito
    // disparava a cada troca de aba, reativando a tela de loading de página inteira
    // (linha abaixo) e desmontando/remontando todo o app.
  }, [user?.id, organization?.id]);

  // Salvar a última rota acessada no localStorage
  useEffect(() => {
    if (location.pathname !== '/' && location.pathname !== '/signin' && location.pathname !== '/signup') {
      localStorage.setItem('lastRoute', location.pathname);
    }
  }, [location]);

  useEffect(() => {
    // Adicionar listener para o evento de mudança de seção
    const handleSectionChange = (event: CustomEvent) => {
      if (event.detail && event.detail.section) {
        navigate(`/${event.detail.section}`);
      }
    };

    window.addEventListener('changeSection', handleSectionChange as EventListener);
    
    return () => {
      window.removeEventListener('changeSection', handleSectionChange as EventListener);
    };
  }, [navigate]);

  // A espera pelos projetos fica só na área de conteúdo (ver abaixo). Trocar o
  // layout inteiro por "Loading..." desmontava e remontava todos os providers e
  // componentes de baixo, e cada um repetia suas consultas iniciais.
  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/signin" />;
  }

  // A raiz não é uma tela: volta para a última usada ou, sem histórico, para Projetos.
  if (location.pathname === '/') {
    const lastRoute = localStorage.getItem('lastRoute');
    const target = lastRoute && lastRoute.startsWith('/') && !['/', '/signin', '/signup', '/reset-password'].includes(lastRoute) ? lastRoute : '/projects';
    return <Navigate to={target} replace />;
  }

  // Derivar a seção diretamente da rota
  const section = getSectionFromPath(location.pathname);

  const handleSectionChange = (section: string) => {
    // Usar o navigate para mudar a URL
    if (section) {
      navigate(`/${section}`);
      // Scroll to top da página quando mudar de seção
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const renderContent = () => {
    switch (section) {
      case 'getting-started':
        return <WelcomeWorkflow onNavigate={handleSectionChange} />;
      case 'company-notifications':
        return <CompanyNotifications />;
      case 'user-management':
        return <AccessManagement />;
      case 'team-costs':
        return <AccessManagement initialTab="team-costs" />;
      case 'company-profile':
        return <CompanyProfile />;
      case 'profile':
        return <ProfileManagement />;
      case 'help':
        return <HelpScreen showManageButton={true} />;
      case 'help-popup':
        return <HelpScreen showManageButton={false} />;
      case 'faq-manager':
        return <FaqManager />;
      case 'price-simulator':
        return <PriceSimulator />;
      case 'tasks':
        return <TasksPage />;
      case 'tasks-unified':
        return <UnifiedTaskView />;
      case 'tasks-summary':
        return <TaskSummaryView />;
      case 'tasks-insights':
        return <TaskInsights />;
      case 'new-task':
        return <InboxView />;
      case 'today':
        return <TodayView />;
      case 'upcoming':
        return <UpcomingView />;
      case 'overdue':
        return <OverdueTasks />;
      case 'filters':
        return <TagsAndFilters />;
      case 'completed':
        return <CompletedTasksView />;
      case 'admin-favorites':
        return <CreateFavoritesTable />;
      case 'my-activities':
        return <StrategicDashboard />;
      case 'activities-control':
      case 'activities':
        return <SimpleKanbanBoard />;
      case 'activities-calendar':
        return <ActivitiesCalendar onUpdateTask={async (task) => {
          try {
            // Aqui você pode implementar a lógica de atualização da tarefa
            // Por exemplo, chamar um serviço ou API
            console.log('Atualizando tarefa:', task);
            // Retornar uma Promise resolvida para satisfazer o tipo
            return Promise.resolve();
          } catch (error) {
            console.error('Erro ao atualizar tarefa:', error);
            return Promise.reject(error);
          }
        }} />;
      case 'activities-strategic':
        return <GanttViewOptimized />;
      case 'projects':
        return <ProjectsList />;
      case 'suppliers-clients-manager':
        return <SuppliersClientsManager />;
      case 'test-products':
        return <TestProductsConnection />;
      case 'test-categories':
        return <TestCategoriesConnection />;
      case 'tracker':
        return <ExternalTimeTracker />;
      case 'time':
        return <TimeControl />;
      case 'calendar':
        return <CalendarView />;
      case 'reports':
        return <UnifiedReports />;
      case 'settings':
        return <Settings />;
      case 'product-spec':
        return <BudgetDashboard />;
      case 'product-spec-products': {
        const currentProjectId = projects.length > 0 ? String(projects[0].id) : 'default-project';
        return <ServicesManager projectId={currentProjectId} projects={projects} section="products" />;
      }
      case 'product-spec-services': {
        const currentProjectId = projects.length > 0 ? String(projects[0].id) : 'default-project';
        return <ServicesManager projectId={currentProjectId} projects={projects} section="services" />;
      }
      case 'product-spec-budget': {
        const currentProjectId = projects.length > 0 ? String(projects[0].id) : 'default-project';
        return <ServicesManager projectId={currentProjectId} projects={projects} section="budget" />;
      }
      case 'product-spec-service-types':
        return <ServiceTypesManager />;
      case 'clients':
        return (
          <ProtectedRoute requirePlan="completo" moduleName="Clientes">
            <Clients />
          </ProtectedRoute>
        );
      case 'tags':
        return <TagsManager />;
      case 'team':
        return <Team />;
      case 'members':
        return <MembersList />;
      case 'nankin-insights':
        return <NankinInsights />;
      case 'sales-pipeline':
        return (
          <ProtectedRoute requirePlan="completo" moduleName="Pipeline de Vendas">
            <SalesPipelineBoard />
          </ProtectedRoute>
        );
      case 'financial-dashboard':
        return (
          <ProtectedRoute requirePlan="completo" moduleName="Dashboard Financeiro">
            <FinancialDashboard />
          </ProtectedRoute>
        );
      case 'financial-projects':
        return (
          <ProtectedRoute requirePlan="completo" moduleName="Projetos Financeiros">
            <FinancialProjects />
          </ProtectedRoute>
        );
      case 'financial-movements':
        return (
          <ProtectedRoute requirePlan="completo" moduleName="Movimentações Financeiras">
            <UnifiedFinancialMovements />
          </ProtectedRoute>
        );
      case 'financial-registers':
        return (
          <ProtectedRoute requirePlan="completo" moduleName="Cadastros Financeiros">
            <FinancialRegisters />
          </ProtectedRoute>
        );
      case 'financial-calendar':
        return (
          <ProtectedRoute requirePlan="completo" moduleName="Agenda Financeira">
            <FinancialCalendar />
          </ProtectedRoute>
        );
      case 'reconciliation':
        return (
          <ProtectedRoute requirePlan="completo" moduleName="Conciliação Bancária">
            <Reconciliation />
          </ProtectedRoute>
        );
      case 'bank-accounts':
        return (
          <ProtectedRoute requirePlan="completo" moduleName="Contas Bancárias">
            <BankAccountsManager />
          </ProtectedRoute>
        );
      default:
        // Verificar se o section é um ID de projeto
        const projectMatch = projects.find(p => String(p.id) === section);
        if (projectMatch) {
          return <ProjectOverview 
            projectId={section} 
            onNavigateBack={() => handleSectionChange('projects')} 
          />;
        }
        return <SimpleTimeTracker />;
    }
  };

  return (
    <ChatPopupProvider>
      <KanbanTimeTrackingProvider>
        <ChatProvider organizationId={organization?.id}>
          <div className="flex flex-col md:flex-row min-h-screen bg-gray-50 overflow-hidden">
            <MetaTitle />
            <Sidebar activeSection={section} onSectionChange={handleSectionChange} projects={projects} />
            <div className="flex-1 w-full overflow-x-hidden">
              <Header activeSection={section} onSectionChange={handleSectionChange} />
              <Suspense fallback={<LoadingFallback />}>
                {projectsLoading
                  ? <LoadingFallback />
                  : <ModuleGate section={section}>{renderContent()}</ModuleGate>}
              </Suspense>
            </div>
            <NankinAIChat section={section} />
            <Suspense fallback={<div></div>}>
              <ChatFloatingButton />
            </Suspense>
            <Suspense fallback={<div></div>}>
              <ChatPopup />
            </Suspense>
            <ChatToastNotifications />
            <KanbanTimeTrackingPopup />
            <div className="fixed bottom-4 left-0 w-72 md:w-80 z-10 flex justify-center">
              <VersionDisplay variant="badge" />
            </div>
            <VersionUpdateNotification />
          </div>
        </ChatProvider>
      </KanbanTimeTrackingProvider>
    </ChatPopupProvider>
  );
};

// const ChatProviderWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
//   const { organization } = useOrganization();
//   return (
//     <ChatProvider organizationId={organization?.id}>
//       {children}
//     </ChatProvider>
//   );
// };

function App() {
  return (
    <HelmetProvider>
      <ErrorBoundary>
        <ToastProvider>
          <ConfirmProvider>
            <Router>
              <AuthProvider>
                <RouteTracker />
                <LanguageProvider>
                  <SettingsProvider>
                    <Routes>
                      {/* Rotas públicas sem contextos de organização */}
                      <Route path="/signin" element={<SignInForm />} />
                      {PUBLIC_SIGNUP_ENABLED && (
                        <Route path="/signup" element={<SignUpForm />} />
                      )}
                      <Route path="/reset-password" element={<ResetPassword />} />

                      {/* Rotas protegidas com todos os contextos */}
                      <Route path="/*" element={
                        <OrganizationProvider>
                          <TagsProvider>
                            <PermissionsProvider>
                              <ModuleAccessProvider>
                                <ProtectedLayout />
                              </ModuleAccessProvider>
                            </PermissionsProvider>
                          </TagsProvider>
                        </OrganizationProvider>
                      } />
                    </Routes>
                  </SettingsProvider>
                </LanguageProvider>
              </AuthProvider>
            </Router>
          </ConfirmProvider>
        </ToastProvider>
      </ErrorBoundary>
    </HelmetProvider>
  );
}

export default App;
